import getDb, { GenieItem, GPTemplate, GLTTemplate, GRefer, GTreatmentPackage } from './db';
import { Patient } from './types/patient';
import { Patient as VisitPt } from './types/visit';
import {
    Doctor,
    DoctorCalendarConfigs,
    Prescription,
    Patient as PxPt,
    TDoctorEvitalsConfigs,
} from './types/prescription';
import { Visit } from './types/visit';
import { DocProfileResponse, Template } from '../api/fetchDoctorProfile';
import { DoctorProfile as MyDoctorProfile } from '../api/fetchMyProfile';
import { PrintConfig, Translate } from '../api/pushVisit';
import { MHC } from './types/medicalHistory';
import { migrateToNew } from './utils';
import {
    DEFAULT_GENIE_SETTINGS,
    DEFAULT_PRINT_CONFIG,
    DEFAULT_PT_COLUMNS,
    DEFAULT_TRANSLATE,
    PtDirLastUpdatedAt,
    PtDirVersion,
    PT_DIR_LAST_UPDATED_AT,
    DEFAULT_PT_DIR_VERSION,
    DEFAULT_GENIE_VERSION,
    DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    DEFAULT_VITALS_CONFIG_VALUES,
    DEFAULT_CALCULATOR_CONFIG_VALUES,
    GenieLastUpdatedAt,
    GINIE_LAST_UPDATED_AT,
    DEFAULT_SECTION_NAME_CONFIG_VALUES,
    ActiveOrderUpdatesLastUpdatedAt,
    ACTIVE_ORDER_UPDATES_LAST_UPDATED_AT,
    ActiveOrderUpdatesCount,
    ACTIVE_ORDER_UPDATES_COUNT,
    MilestoneUpdatedAt,
    BackgroundConfig,
} from '../constants';
import { FormData, DFormEntity } from '../api/fetchDoctorGenieByOID';
import {
    CalculatorsConfig,
    ExtraFlags,
    GeniePtColumns,
    GenieSettings,
    MedicalHistoryConfig,
    SR,
    VitalsConfig,
} from '../api/pushGenie';
import { setupAppzi, setUpChatSession, setupUserInSentry } from '../App';
import { setTrackingIdentify } from '../tracking';
import {
    Inventory,
    InventoryItem,
} from '../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import { TemplateConfig } from '../Templar/RenderPdfPrescription';
import { AccountResponse, LS_DOCTOR_ACCOUNTS } from '../api/fetchDocAccounts';
import { Order } from '../api/fetchAllHEOrder';
import { IMilestoneData } from '../api/getMilestone';
import moment from 'moment';
import { MyDoc, MyProfileResponse } from '../api/fetchMyProfile';
import {
    getActiveDoctorId,
    getMActiveId,
    LS_KEY_MY_PROFILE,
    setIsAllClinics,
} from './localstorage';
import { getMyDbId } from '../api/mactiveid';
import Dexie, { Table } from 'dexie';
import { DB_TABLES, NON_GENIE_TABLES } from './dbTables';
import {
    BizSettings,
    PtDir,
    ReferralConfig,
    Role,
    TreatmentServicesAndTreatmentPackages,
} from '../api/fetchMyGenie';
import { Appointment } from '../api/fetchAppointmentById';
import { OfflineAppointment } from '../firestore/appointments';
import { RenderPdfConfig } from '../Templar/types';

export const ALREADY_EXISTS_ERROR = {
    code: 0,
    message: 'Key already exists in the object store.',
    name: 'ConstraintError',
};

export const QUOTA_EXCEEDED_ERR = {
    code: 1,
    message: 'Local Storage Quota Exceeded',
    name: 'QUOTA_EXCEEDED_ERR',
};

const getTable = (name: DB_TABLES, id: string | undefined = getMyDbId()): Table | undefined => {
    if (!id) {
        throw new Error('user db not found');
    }
    const db = getDb(id);
    if (!db) {
        throw new Error('db not found');
    }
    return db.table(name);
};

export const getVisitById = async (id: string): Promise<Visit> => {
    const visit = await getTable(DB_TABLES.visits, getActiveDoctorId() || getMActiveId())?.get(id);
    return visit;
};

export const getCompletedVisitById = async (id: string): Promise<Visit> => {
    const visit = await getTable(DB_TABLES.visits, getActiveDoctorId() || getMActiveId())
        ?.where({
            id: id,
            isCompleted: 1,
        })
        .first();
    return visit;
};

export const getVisitByIds = async (ids: string[]): Promise<Visit[] | undefined> => {
    const visit = await getTable(DB_TABLES.visits)?.bulkGet(ids);
    return visit;
};

export const addVisit = async (visit: Visit): Promise<void> => {
    await getTable(DB_TABLES.visits, getActiveDoctorId() || getMActiveId())?.add(visit);
};
export const updateLastOningVisitForPatient = async (
    plId: string,
    visitId: string,
): Promise<void> => {
    await getTable(DB_TABLES.patients, getActiveDoctorId() || getMActiveId())?.update(plId, {
        lastGoingVisit: visitId,
    });
};

export const updatePrescriptionUrlAndVisitNameInVisit = async (
    visitId: string,
    name: string,
    url?: string,
): Promise<void> => {
    await getTable(DB_TABLES.visits)?.update(visitId, {
        prescriptionUrl: url,
        name: name,
        status: 'CM',
    });
};

export const markPrescriptionAsPushFailedStatus = async (
    pxId: string,
    status: 0 | 1,
): Promise<void> => {
    await getTable(DB_TABLES.prescriptions)?.update(pxId, { _is_failed: status });
};

export const getAllUnpushedPrescriptions = async (): Promise<Prescription[] | undefined> => {
    return await getTable(DB_TABLES.prescriptions)?.where('_is_failed').equals(1).toArray();
};

export const updatePtInVisit = async (visitId: string, pt: VisitPt): Promise<void> => {
    await getTable(DB_TABLES.visits)?.update(visitId, { patient: pt });
};

export const updatePtInPx = async (pxId: string, pt: PxPt): Promise<void> => {
    await getTable(DB_TABLES.prescriptions)?.update(pxId, { patient: pt });
};

export const updateMhInPx = async (pxId: string, mh: MHC): Promise<void> => {
    await getTable(DB_TABLES.prescriptions)?.update(pxId, {
        patientHistory: mh,
        isPatientHistoryFetchFailed: false,
    });
};

export const getMilestones = async (docid: string): Promise<IMilestoneData[] | undefined> => {
    return await getTable(DB_TABLES.milestones, docid)?.toArray();
};

export const clearAllMilestones = async (docid: string): Promise<void> => {
    return await getTable(DB_TABLES.milestones, docid)?.clear();
};

export const bulkPutMilestones = async (docid: string, values: IMilestoneData[]): Promise<void> => {
    await getTable(DB_TABLES.milestones, docid)?.bulkPut(values);
};

export const putMilestones = async (docid: string, value: IMilestoneData): Promise<void> => {
    await getTable(DB_TABLES.milestones, docid)?.put(value);
};

export const updateMhErrorInPx = async (pxId: string): Promise<void> => {
    await getTable(DB_TABLES.prescriptions)?.update(pxId, { isPatientHistoryFetchFailed: true });
};

export const upsertVisits = async (visits: Visit[]): Promise<void> => {
    await getTable(DB_TABLES.visits)?.bulkPut(visits);
};

export const getAllVisits = async (): Promise<Visit[] | undefined> => {
    return await getTable(DB_TABLES.visits)?.toArray();
};

export const getAllPrescription = async (): Promise<Prescription[] | undefined> => {
    return await getTable(DB_TABLES.prescriptions)?.toArray();
};

export const getPrescriptionById = async (id: string): Promise<Prescription> => {
    return await getTable(DB_TABLES.prescriptions, getActiveDoctorId() || getMActiveId())?.get(id);
};

export const getPrescriptionsByVisitId = async (
    ...visitId: string[]
): Promise<Prescription[] | undefined> => {
    return await getTable(DB_TABLES.prescriptions, getActiveDoctorId() || getMActiveId())
        ?.where('visitId')
        .anyOf(visitId)
        .toArray();
};

export const getAllVisitsByPatientLocalId = async (
    ...id: string[]
): Promise<Visit[] | undefined> => {
    return await (
        await getTable(DB_TABLES.visits)
            ?.where('patient.localId')
            .anyOf(id)
            .and((v) => v.isCompleted === 1)
            .sortBy('createdAt')
    )?.reverse();
};

export const upsertPatient = async (patient: Patient): Promise<void> => {
    await getTable(DB_TABLES.patients)?.put(patient);
};

export const deletePatientByLocalId = async (localId: string): Promise<void> => {
    await getTable(DB_TABLES.patients)?.delete(localId);
};

export const deleteAllPatients = async (): Promise<void> => {
    await getTable(DB_TABLES.patients)?.clear();
};

export const deleteActiveOrderUpdates = async (): Promise<void> => {
    await getTable(DB_TABLES.activeOrderUpdates)?.clear();
};

export const upsertPatients = async (patients: Patient[]): Promise<void> => {
    await getTable(DB_TABLES.patients, getMActiveId())?.bulkPut(patients);
};

export const upsertActiveOrderUpdates = async (orders: Order[]): Promise<void> => {
    await getTable(DB_TABLES.activeOrderUpdates)?.bulkPut(orders);
};

export const getAllPatients = async (
    limit?: number,
    offset?: number,
): Promise<Patient[] | undefined> => {
    const allPatientsCount = await getTable(DB_TABLES.patients)
        ?.orderBy('updatedAt')
        .offset(offset || 0)
        .limit(limit || Number.MAX_SAFE_INTEGER)
        .reverse()
        .toArray();
    return allPatientsCount;
};

export const getActiveOrderUpdates = async (): Promise<Order[] | undefined> => {
    const allActiveOrderUpdates = await getTable(DB_TABLES.activeOrderUpdates)
        ?.orderBy('updated_at')
        .reverse()
        .toArray();
    return allActiveOrderUpdates;
};

export const getAllPatientsUsingPids = async (pids: string[]): Promise<Patient[] | undefined> => {
    const allPatientsCount = await getTable(DB_TABLES.patients)
        ?.where('localId')
        .anyOf(pids)
        .toArray();
    return allPatientsCount;
};

export const getAllPatientsWithFollowupOn = async (
    startDate: Date,
    endDate?: Date,
): Promise<Patient[] | undefined> => {
    const startToday = new Date(startDate.setHours(0, 0, 0, 0));
    const endToday = new Date((endDate || startDate).setHours(23, 59, 59, 999));
    const allPatientsCount = await getTable(DB_TABLES.patients)
        ?.where('followUpDate')
        .between(startToday, endToday)
        .reverse()
        .toArray();
    return allPatientsCount;
};

export const getAllPatientsAfterAMonth = async (
    currmonth: moment.Moment,
): Promise<Patient[] | undefined> => {
    const startToday = new Date(currmonth.toISOString());
    const endToday = new Date(currmonth.add(1, 'month').toISOString());

    const allPatientsCount = await getTable(DB_TABLES.patients)
        ?.where('createdAt')
        .between(startToday, endToday)
        .reverse()
        .toArray();
    return allPatientsCount;
};

export const getPatientByLocalId = async (id: string): Promise<Patient> => {
    const patient = await getTable(DB_TABLES.patients, getActiveDoctorId() || getMActiveId())?.get(
        id,
    );
    return patient;
};

export const getAllPatientsPhoneStartsWith = async (
    phone: string,
): Promise<Patient[] | undefined> => {
    const allPatients = await getTable(DB_TABLES.patients)
        ?.where('personal.phone.n')
        .startsWith(phone)
        .limit(10)
        .sortBy('updatedAt');
    return allPatients;
};

export const addPrescriptionIfNotExist = async (prescription: Prescription): Promise<void> => {
    try {
        await getTable(DB_TABLES.prescriptions, getActiveDoctorId() || getMActiveId())?.add(
            prescription,
        );
    } catch (e) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (e.inner.code !== ALREADY_EXISTS_ERROR.code) throw e;
    }
};

export const upsertPrescription = async (prescription: Prescription): Promise<void> => {
    await getTable(DB_TABLES.prescriptions, getActiveDoctorId() || getMActiveId())?.put(
        prescription,
    );
};

export const completeVisitById = async (visitId: string): Promise<void> => {
    await getTable(DB_TABLES.visits)?.update(visitId, { isCompleted: 1, endedAt: new Date() });
};

export const updateLastVisitAndFollowUpInThePatient = async (
    patientId: string,
    followUpdate?: string,
): Promise<void> => {
    const updates = {
        lastVisitedAt: new Date(),
        followUpDate: followUpdate ? new Date(followUpdate) : undefined,
        lastGoingVisit: undefined,
    };

    await getTable(DB_TABLES.patients)?.update(patientId, updates);
};

type updateItem = GenieItem | OfflineAppointment;
export const updateG = async <ItemType extends updateItem>(
    id: string,
    entity: DB_TABLES,
    items: ItemType[],
): Promise<void> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }
    try {
        await table.bulkPut(items);
    } catch (e) {
        console.error('Error in updateG', entity, JSON.stringify(items), e);
    }
};

export const getGCount = async (id: string, entity: DB_TABLES): Promise<number> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }

    const count = await table.count();
    return count;
};

export const purgeG = async (id: string, entity: DB_TABLES, maxLimit: number): Promise<void> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }
    const nonPurgeableItemsCount = await table.filter((i) => i.meta?.do_not_purge).count();
    await table
        .orderBy('meta.updatedAt')
        .filter((i) => !i.meta?.do_not_purge)
        .reverse()
        .offset(maxLimit - nonPurgeableItemsCount)
        .delete();
};

export const getGLocal = async <ItemType>(id: string, entity: DB_TABLES): Promise<ItemType[]> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }
    return await (await table.where('meta.isLocal').equals(1).sortBy('meta.fCount')).reverse();
};

export const getG = async <ItemType>(
    id: string,
    entity: DB_TABLES,
    limit: number = 3,
): Promise<ItemType[]> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }
    if (limit >= 0) {
        return await table.orderBy('meta.fCount').reverse().limit(limit).toArray();
    } else {
        return await table.orderBy('meta.fCount').reverse().toArray();
    }
};

export const getGById = async <ItemType>(
    dbid: string,
    entity: DB_TABLES,
    id: string,
): Promise<ItemType> => {
    const table = getTable(entity, dbid);
    if (!table) {
        throw new Error('table not found');
    }

    return await table.get(id);
};

export const getGArray = async <ItemType>(id: string, entity: DB_TABLES): Promise<ItemType[]> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }

    return await table.toArray();
};

export const deleteG = async (dbid: string, entity: DB_TABLES, id: string): Promise<void> => {
    const table = getTable(entity, dbid);
    if (!table) {
        throw new Error('table not found');
    }

    return await table.delete(id);
};

export type TemplateMeta = { id: string; name: string; subString?: string; isPackage?: boolean };

export const clearTable = async (id: string, entity: DB_TABLES): Promise<void> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }

    return await table.clear();
};

export const getTemplatesMeta = async (id: string, entity: DB_TABLES): Promise<TemplateMeta[]> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }

    const templates: (GPTemplate | GLTTemplate)[] = await table
        .orderBy('meta.updatedAt')
        .reverse()
        .toArray();
    return templates.map((t) => ({
        id: t.id,
        name: t.name,
        subString: Object.entries(t)
            .map(([, value]) => {
                if (Array.isArray(value)) {
                    return value.map((l) => l.name).join(', ');
                }
            })
            .filter(Boolean)
            .join(', '),
        isPackage: t.isPackage,
    }));
};

export const getGenieItemById = async (
    dbid: string,
    entity: DB_TABLES,
    id: string,
): Promise<GPTemplate | GLTTemplate> => {
    const table = getTable(entity, dbid);
    if (!table) {
        throw new Error('table not found');
    }

    return await table.get(id);
};

export const updateDoctorProfile = async (profile: MyProfileResponse): Promise<void> => {
    try {
        setTrackingIdentify(profile);
    } catch (err) {
        console.error(err);
    }

    try {
        if (profile.docs?.length) {
            await getTable(DB_TABLES.doctors, getMActiveId() || '')?.bulkPut(
                profile.docs.map((d) => ({
                    _id: d.id,
                    profile: { personal: d.personal, professional: d.professional },
                })),
            );
        }

        if (profile.doctor) {
            await getTable(DB_TABLES.doctors, getMActiveId() || '')?.put(profile.doctor);
        }
    } catch (err) {}

    try {
        if (window) {
            // @deprecated, move to LS_KEY_MY_PROFILE
            localStorage.setItem('docProfile', JSON.stringify(profile.doctor));

            localStorage.setItem(LS_KEY_MY_PROFILE, JSON.stringify(profile));
            setupAppzi();
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window.hj) {
            const userId = profile.doctor?._id || null;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.hj('identify', userId, {
                'name.f': profile.doctor?.profile.personal.name.f,
                role: profile.loginmeta?.role,
                category: profile.doctor?.profile.professional.category?.join(', '),
            });
        }

        setUpChatSession();
        setupUserInSentry();
    } catch (e) {
        console.error(e);
    }
    // await db.table('profile').put(profile);
};

export const deleteDb = async (): Promise<void> => {
    try {
        const databases = await Dexie.getDatabaseNames();
        console.log('databases', databases);
        for (const db of databases) {
            if (db.startsWith('docapp')) {
                console.log('deleting db', db);
                await Dexie.delete(db);
            }
        }
    } catch (e) {
        console.error(e);
    }

    const dbId = getMyDbId();
    if (!dbId) {
        return;
    }
    const db = getDb(dbId);
    if (!db) {
        throw new Error('db not found');
    }
    await db.delete();
};

const DB_PRINT_CONFIG_KEY = 'printConfig';

/**
 * @deprecated
 * TOTO use getItemFromMisc instead
 * */
export const upsertPrintConfig = async (docid: string, printConfig: PrintConfig): Promise<void> => {
    await getTable(DB_TABLES.DB_TABLE_MISC, docid)?.put({ key: DB_PRINT_CONFIG_KEY, printConfig });
};

export const getPrintConfig = async (docid: string): Promise<PrintConfig> => {
    const result = await getTable(DB_TABLES.DB_TABLE_MISC, docid)?.get(DB_PRINT_CONFIG_KEY);
    return result && result.printConfig ? migrateToNew(result.printConfig) : DEFAULT_PRINT_CONFIG;
};

const DB_PT_COLUMNS_KEY = 'ptColumns';

export const updatePtColumns = async (id: string, ptColumns: GeniePtColumns): Promise<void> => {
    await getTable(DB_TABLES.DB_TABLE_MISC, id)?.put({
        key: DB_PT_COLUMNS_KEY,
        [DB_PT_COLUMNS_KEY]: ptColumns,
    });
};

export const getPtColumns = async (): Promise<GeniePtColumns> => {
    const id = getActiveDoctorId() || getMActiveId() || '';
    const result = await getTable(DB_TABLES.DB_TABLE_MISC, id)?.get(DB_PT_COLUMNS_KEY);
    return result && result[DB_PT_COLUMNS_KEY] ? result[DB_PT_COLUMNS_KEY] : DEFAULT_PT_COLUMNS;
};

export const getGReferDocForSpeciality = async (
    docid: string,
    s_id: string,
    limit?: number,
): Promise<GRefer[] | undefined> => {
    let gReferResult;

    if (limit) {
        gReferResult = await getTable(DB_TABLES.refer, docid)
            ?.where('s_id')
            .equals(s_id)
            .limit(limit)
            .sortBy('meta.fCount');
    } else {
        gReferResult = await getTable(DB_TABLES.refer, docid)
            ?.where('s_id')
            .equals(s_id)
            .sortBy('meta.fCount');
    }

    return await gReferResult?.reverse();
};

export const getGReferDoc = async (docid: string): Promise<GRefer[] | undefined> => {
    return await getTable(DB_TABLES.refer, docid)?.toArray();
};

const DB_SETTINGS_KEY = 'settings';
const DB_PT_DIR_LAST_UPDATED_KEY = 'ptDirLastUpdated';
const DB_ACTIVE_ORDER_UPDATES_LAST_UPDATED_KEY = 'activeOrderUpdatesLastUpdated';
const DB_ACTIVE_ORDER_UPDATES_COUNT_KEY = 'activeOrderUpdatesCount';
const DB_GENIE_LAST_UPDATED_KEY = 'genieLastUpdated';
const DB_TRANSLATE_KEY = 'translate';
const DB_PT_DIR_VERSION = 'ptDirVersion';
const DB_GENIE_VERSION = 'genieVersion';
const DB_MEDICAL_HISTORY_CONFIG = 'medicalHistoryConfig';
const DB_VITALS_CONFIG = 'vitalsConfig';
const DB_CALCULATOR_CONFIG = 'calculatorConfig';
const SECTION_NAME_CONFIG = 'sectionNameConfig';
const STAFF_RESTRICTION = 'staffRestriction';
const KEY_VITALS = 'keyVitals';
const EXTRA_FLAGS = 'extraFlags';
const MILESTONE_UPDATED_AT = 'milestonesLastUpdatedAt';
const DB_TREATMENT_SERVICES_AND_TREATMENT_PACKAGES = 'treatmentServicesAndTreatmentPackages';
const CALENDAR_CONFIGS = 'calendarConfigs';
const DB_DICOM = 'dicom';
const DB_IPD = 'ipd';
const DB_PTDIR = 'ptDir';
const DB_BIZ_SETTINGS = 'bizSettings';
const DB_REFERRAL = 'referralConfig';

export enum MISC_KEYS {
    'settings' = 'settings',
    'ptDirLastUpdated' = 'ptDirLastUpdated',
    'activeOrderUpdatesLastUpdated' = 'activeOrderUpdatesLastUpdated',
    'activeOrderUpdatesCount' = 'activeOrderUpdatesCount',
    'genieLastUpdated' = 'genieLastUpdated',
    'translate' = 'translate',
    'ptDirVersion' = 'ptDirVersion',
    'genieVersion' = 'genieVersion',
    'medicalHistoryConfig' = 'medicalHistoryConfig',
    'vitalsConfig' = 'vitalsConfig',
    'calculatorConfig' = 'calculatorConfig',
    'sectionNameConfig' = 'sectionNameConfig',
    'staffRestriction' = 'staffRestriction',
    'keyVitals' = 'keyVitals',
    'milestonesLastUpdatedAt' = 'milestonesLastUpdatedAt',
    'extraFlags' = 'extraFlags',
    'treatmentServicesAndTreatmentPackages' = 'treatmentServicesAndTreatmentPackages',
    'calendarConfigs' = 'calendarConfigs',
    'evitalsConfig' = 'evitalsConfig',
    'dicom' = 'dicom',
    'ipd' = 'ipd',
    'referralConfig' = 'referralConfig',
    'ptDir' = 'ptDir',
    'bizSettings' = 'bizSettings',
    'backgroundConfig' = 'backgroundConfig',
    'groupAccess' = 'groupAccess',
}

export const misc: {
    [k in keyof typeof MISC_KEYS]: {
        key: string;
        defaultValue:
            | GenieSettings
            | PtDirLastUpdatedAt
            | ActiveOrderUpdatesLastUpdatedAt
            | ActiveOrderUpdatesCount
            | GenieLastUpdatedAt
            | Translate
            | PtDirVersion
            | MedicalHistoryConfig
            | VitalsConfig
            | CalculatorsConfig
            | SR[]
            | ExtraFlags
            | MilestoneUpdatedAt
            | TreatmentServicesAndTreatmentPackages
            | DoctorCalendarConfigs
            | TDoctorEvitalsConfigs
            | PtDir
            | BizSettings
            | ReferralConfig
            | BackgroundConfig;
    };
} = {
    [DB_SETTINGS_KEY]: { key: 'setting', defaultValue: DEFAULT_GENIE_SETTINGS },
    [DB_PT_DIR_LAST_UPDATED_KEY]: { key: 'ptDirLastUpdated', defaultValue: PT_DIR_LAST_UPDATED_AT },
    [MILESTONE_UPDATED_AT]: {
        key: 'milestonesLastUpdatedAt',
        defaultValue: { isoString: undefined },
    },
    [DB_ACTIVE_ORDER_UPDATES_LAST_UPDATED_KEY]: {
        key: 'activeOrderUpdatesLastUpdated',
        defaultValue: ACTIVE_ORDER_UPDATES_LAST_UPDATED_AT,
    },
    [DB_ACTIVE_ORDER_UPDATES_COUNT_KEY]: {
        key: 'activeOrderUpdatesCount',
        defaultValue: ACTIVE_ORDER_UPDATES_COUNT,
    },
    [DB_GENIE_LAST_UPDATED_KEY]: { key: 'genieLastUpdated', defaultValue: GINIE_LAST_UPDATED_AT },
    [DB_TRANSLATE_KEY]: { key: 'translate', defaultValue: DEFAULT_TRANSLATE },
    [DB_PT_DIR_VERSION]: { key: DB_PT_DIR_VERSION, defaultValue: DEFAULT_PT_DIR_VERSION },
    [DB_GENIE_VERSION]: { key: DB_GENIE_VERSION, defaultValue: DEFAULT_GENIE_VERSION },
    [DB_MEDICAL_HISTORY_CONFIG]: {
        key: DB_MEDICAL_HISTORY_CONFIG,
        defaultValue: DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    },
    [DB_VITALS_CONFIG]: {
        key: DB_VITALS_CONFIG,
        defaultValue: DEFAULT_VITALS_CONFIG_VALUES,
    },
    [DB_CALCULATOR_CONFIG]: {
        key: DB_CALCULATOR_CONFIG,
        defaultValue: DEFAULT_CALCULATOR_CONFIG_VALUES,
    },
    [SECTION_NAME_CONFIG]: {
        key: SECTION_NAME_CONFIG,
        defaultValue: DEFAULT_SECTION_NAME_CONFIG_VALUES,
    },
    [STAFF_RESTRICTION]: {
        key: STAFF_RESTRICTION,
        defaultValue: [],
    },
    [KEY_VITALS]: {
        key: KEY_VITALS,
        defaultValue: [],
    },
    [EXTRA_FLAGS]: {
        key: EXTRA_FLAGS,
        defaultValue: {
            substitution: undefined,
            balance: undefined,
        },
    },
    [DB_TREATMENT_SERVICES_AND_TREATMENT_PACKAGES]: {
        key: DB_TREATMENT_SERVICES_AND_TREATMENT_PACKAGES,
        defaultValue: {
            enabled: false,
            treatment_services_fetched_at: undefined,
        },
    },
    [CALENDAR_CONFIGS]: {
        key: CALENDAR_CONFIGS,
        defaultValue: {
            slotsFromDoctorCalendar: false,
        },
    },
    [MISC_KEYS['evitalsConfig']]: {
        key: MISC_KEYS['evitalsConfig'],
        defaultValue: {
            manualPushOrder: false,
        },
    },
    [DB_DICOM]: {
        key: DB_DICOM,
        defaultValue: {
            enabled: false,
        },
    },
    [DB_IPD]: {
        key: DB_IPD,
        defaultValue: {
            enabled: false,
        },
    },
    [DB_PTDIR]: {
        key: DB_PTDIR,
        defaultValue: {
            userLevel: false,
            disableAllPtFetch: false,
            enableBEGlobalSearch: false,
        },
    },
    [DB_BIZ_SETTINGS]: {
        key: DB_BIZ_SETTINGS,
        defaultValue: {
            summaryPdfLocalDisabled: false,
            searchAndAddPatient: true,
            fixPdfIconEnabled: false,
            showPurchaseEkaCreditsPopover: true,
            localPrintDisabled: true,
            showNewSignInStrip: true,
        },
    },
    [DB_REFERRAL]: {
        key: DB_REFERRAL,
        defaultValue: {
            mobileOptional: false,
            withinBusiness: false,
        },
    },
    [MISC_KEYS['backgroundConfig']]: {
        key: MISC_KEYS['backgroundConfig'],
        defaultValue: {
            backgroundImage: {
                id: '',
                name: '',
                type: '',
                dataUrl: '',
            },
            lastSelectedTeleBg: {
                option: 'no-background',
            },
        },
    },
    [MISC_KEYS['groupAccess']]: {
        key: MISC_KEYS['groupAccess'],
        defaultValue: {
            enabled: false,
        },
    },
};

export const updateItemInMisc = async <T>(
    docid: string,
    key: keyof typeof MISC_KEYS,
    data: T,
): Promise<void> => {
    await getTable(DB_TABLES.DB_TABLE_MISC, docid)?.put({ key: key, [key]: data });
};

export const getItemFromMisc = async <T>(
    docid: string,
    key: keyof typeof MISC_KEYS,
): Promise<T> => {
    const result = await getTable(DB_TABLES.DB_TABLE_MISC, docid)?.get(key);
    return result && result[key]
        ? // Populate default values
          Array.isArray(result[key]) && Array.isArray(misc[key].defaultValue)
            ? // @ts-expect-error ts version upgrade
              [...(misc[key].defaultValue as Array<string>), ...result[key]]
            : { ...misc[key].defaultValue, ...result[key] }
        : // @ts-expect-error ts version upgrade
          misc[key].defaultValue;
};

export const updateDForms = async (data: FormData): Promise<void> => {
    for (const [key, value] of Object.entries(data)) {
        getTable(DB_TABLES.DB_TABLE_D_FORMS)?.put({ key: key, value: value });
    }
};

export const getDForm = async (key: string): Promise<DFormEntity[]> => {
    const item = await getTable(DB_TABLES.DB_TABLE_D_FORMS)?.get(key);
    return item?.value || [];
};

export const getInventory = async (): Promise<Inventory | undefined> => {
    const item: InventoryItem[] | undefined = await getTable(DB_TABLES.inventory)?.toArray();
    const result = item?.reduce(
        (obj, cur) => ({
            ...obj,
            [cur.mdb_id]: {
                mdb_id: cur.mdb_id,
                quantity: cur.quantity,
                mrp: cur.mrp,
                sku: cur.sku,
                in_stock: cur?.in_stock,
                evitalId: cur.evitalId,
            },
        }),
        {},
    );

    return result;
};

export const getInventoryV2 = async (clinicId: string): Promise<Inventory | undefined> => {
    const item: InventoryItem[] | undefined = await getTable(DB_TABLES.inventoryV2)?.get(clinicId);

    if (!item) {
        return undefined;
    }

    const result = Object.values(item).reduce((obj, cur) => {
        if (!cur?.mdb_id) {
            return obj;
        }

        return {
            ...obj,
            [cur.mdb_id]: {
                mdb_id: cur.mdb_id,
                quantity: cur.quantity,
                mrp: cur.mrp,
                sku: cur.sku,
                in_stock: cur?.in_stock,
                evitalId: cur.evitalId,
            },
        };
    }, {});

    return result;
};

// TODO not related to idb

export const getMe = (): MyProfileResponse | undefined => {
    try {
        const lsMyProfile = localStorage.getItem(LS_KEY_MY_PROFILE);
        const myProfile: MyProfileResponse = lsMyProfile && JSON.parse(lsMyProfile);
        return myProfile;
    } catch (e) {}
};

/**
 * @deprecated
 */
export const isIDristi = (): boolean => {
    return getMe()?.user['b-id'] === '75861499243853';
    // return true;
};

export const getDoctorSelectorList = (): MyDoc[] => {
    const me = getMe();
    let docList: MyDoc[] = me?.docs || [];
    if (me?.doctor) {
        docList = [
            ...docList,
            {
                id: me.doctor._id,
                personal: me.doctor.profile.personal,
                professional: {
                    ...me.doctor.profile.professional,
                    clinics: me.doctor.profile.professional.clinics?.map((c) => ({
                        ...c,
                        id: c._id,
                    })),
                },
            },
        ].filter((d) => d);
    }
    return docList;
};

export const isDoctorSelectorPossible = (): boolean => getDoctorSelectorList().length > 1;

export const getDocIds = (): string[] => {
    const me = getMe();
    const myDocsIds = [...(me?.docs?.map((t) => t.id) || [])];
    if (me?.doctor?._id && !myDocsIds.includes(me?.doctor?._id)) {
        myDocsIds.push(me?.doctor?._id);
    }
    return myDocsIds;
};

export const getMyClinics = ():
    | { _id: string; name: string; abdm?: { hfr_id?: string } }[]
    | undefined => {
    try {
        const me = getMe();
        return me?.clinics?.map((c) => ({ ...c, _id: c.id }));
    } catch (e) {}
};

export const getDoctorProfile = async (id: string): Promise<MyDoctorProfile | undefined> => {
    let doc = undefined;
    try {
        doc = await getGById<MyDoctorProfile | undefined>(
            getMActiveId() || '',
            DB_TABLES.doctors,
            id,
        );
    } catch (e) {
        console.error('Error in getDoctorProfile', id, getMActiveId() || '', e);
    }

    return doc;
};

/**
 * @deprecated Use getSingleDoctor
 * @returns
 */
export const getDoc = (): DocProfileResponse | undefined => {
    try {
        const lsMyProfile = localStorage.getItem(LS_KEY_MY_PROFILE);

        if (lsMyProfile) {
            const myProfile: MyProfileResponse = JSON.parse(lsMyProfile);
            if (myProfile && myProfile.doctor) {
                const myDoctorProfile: DocProfileResponse = myProfile.doctor;
                // for backward compatibility
                myDoctorProfile.loginmeta = myProfile.loginmeta;
                return myDoctorProfile;
            }
        }

        // @deprecated
        const docProfile = localStorage.getItem('docProfile');

        if (!docProfile) {
            console.error('Doc not found');
            return undefined;
        }

        const doc = JSON.parse(docProfile);
        return doc;
    } catch (e) {}
};

export const getSingleDoctor = (): DocProfileResponse | undefined => {
    const me = getMe();
    let doc = me?.doctor;

    if (!doc && isSingleDoctorAccess() && me?.docs?.[0]) {
        doc = {
            _id: me.docs[0].id,
            profile: {
                personal: me.docs[0].personal,
                professional: me.docs[0].professional,
            },
            created_at: '',
            updated_at: '',
        };
    }

    return doc;
};

export const isSingleDoctorAccess = (): boolean => {
    const me = getMe();
    return (me?.doctor && !me.docs?.length) || (!me?.doctor && me?.docs?.length === 1) || false;
};

export const getMySingleDoctorId = (): string | undefined => {
    const me = getMe();
    return me?.doctor?._id || me?.docs?.[0]?.id;
};

export const amIDoctor = (): boolean => {
    const me = getMe();
    return !!me?.doctor;
};

export const amIBusinessUser = (): boolean => {
    const me = getMe();
    return !!me?.user?.['b-id'];
};

export const amIDoctorOnly = (): boolean => {
    const me = getMe();
    return !!me?.doctor && !me.docs?.length;
};

export const getDocAccounts = (): AccountResponse['data'] => {
    const accounts = localStorage.getItem(LS_DOCTOR_ACCOUNTS)
        ? JSON.parse(localStorage.getItem(LS_DOCTOR_ACCOUNTS) || '[]')
        : [];

    return accounts;
};

// TODO not related to idb
export const getDefaultClinicId = (): string | undefined => {
    try {
        const doc: Doctor | undefined = getDoc();
        const me: MyProfileResponse | undefined = getMe();
        return doc?.profile.professional.default_clinic || me?.clinics?.[0]?.id;
    } catch (e) {}
};

export const getTemplates = (): { print?: Template; send?: Template } | undefined => {
    try {
        const doctor = getDoc();

        return {
            print:
                doctor?.profile.professional.templates?.print &&
                doctor?.profile.professional.templates?.print[0],
            send:
                doctor?.profile.professional.templates?.send &&
                doctor?.profile.professional.templates?.send[0],
        };
    } catch (e) {
        console.error(e);
    }
    return;
};

export const getTemplateV2 = async (
    docid: string,
    clinicId: string,
    type: 'PRINT' | 'SEND',
): Promise<TemplateConfig | undefined> => {
    const doc = await getDoctorProfile(docid);
    const doctorTemplateData = doc?.profile.professional.templates_v2;

    const template_v2 = doctorTemplateData?.find(
        (data) => data.clinicId === clinicId && data.type === type,
    );

    return template_v2;
};

export const getDefaultTemplateV2 = (
    clinicId: string,
    type: 'PRINT' | 'SEND',
): RenderPdfConfig | undefined => {
    const doc = getDoc();
    const doctorTemplateData = doc?.profile.professional.templates_v2;

    const template_v2 = doctorTemplateData?.find(
        (data) => data.clinicId === clinicId && data.type === type,
    );

    return template_v2;
};

export const setSelectedClinicId = (id?: string): void => {
    setIsAllClinics(id === 'all');
    if (id === 'all') {
        return;
    }
    if (id) {
        localStorage.setItem('selectedClinicId', id);
    } else {
        localStorage.removeItem('selectedClinicId');
    }
};

export const getSelectedClinicId = (): string | undefined => {
    const id = localStorage.getItem('selectedClinicId');
    if (id) {
        const c = getMe()?.clinics?.find((i) => i.id === id);
        if (c) {
            return id;
        }
    }

    const defaultClinicId = getDefaultClinicId();
    setSelectedClinicId(defaultClinicId);

    return defaultClinicId;
};

export const deleteGenieTable = async (id: string): Promise<void> => {
    // TODO move to Promise.all
    Object.keys(DB_TABLES).forEach(async (key) => {
        const tableName = DB_TABLES[key as keyof typeof DB_TABLES];
        if (tableName in NON_GENIE_TABLES) {
            await getTable(tableName, id)?.clear();
        }
    });
};

export const getPtCount = (): string | null => {
    return localStorage.getItem('ptCount');
};

export const setPtCount = (count?: number | null | undefined): void => {
    if (!count) {
        localStorage.removeItem('ptCount');
        return;
    }
    localStorage.setItem('ptCount', count.toString());
};

export const upsertTreatmentPackage = async (
    treatmentPackage: GTreatmentPackage,
): Promise<void> => {
    await getTable(DB_TABLES.treatmentPackages)?.put(treatmentPackage);
};

// Only for treatment services and treatment packages
export const getGArrayWithClinicId = async <ItemType>(
    id: string,
    entity: DB_TABLES,
    clinic_id: string,
): Promise<ItemType[]> => {
    const table = getTable(entity, id);
    if (!table) {
        throw new Error('table not found');
    }
    return await table.where('clinic_id').equals(clinic_id).toArray();
};

export const getRoles = async (id: string, roles: string[]): Promise<Role[] | undefined> => {
    const allRoles = await getTable(DB_TABLES.roles, id)?.where('id').anyOf(roles).toArray();
    return allRoles;
};

export const getIdbAppointmentById = async (aid: string): Promise<Appointment> => {
    const id = getActiveDoctorId() || getMActiveId() || '';
    const result = await getTable(DB_TABLES.todayAppointments, id)?.get(aid);
    return result;
};

export async function getBackgroundConfig(): Promise<{ file: File | null; bgOption: string }> {
    try {
        const activeId = getMActiveId() || '';
        const existingConfig: BackgroundConfig = (await getItemFromMisc(
            activeId,
            MISC_KEYS.backgroundConfig,
        )) || {
            backgroundImage: { id: '', name: '', type: '', dataUrl: '' },
            lastSelectedTeleBg: { option: 'no-background' },
        };

        let file: File | null = null;
        if (existingConfig.backgroundImage?.dataUrl) {
            const { name, type, dataUrl } = existingConfig.backgroundImage;
            file = await fetch(dataUrl)
                .then((response) => response.blob())
                .then((blob) => new File([blob], name, { type }));
        }

        return { file, bgOption: existingConfig.lastSelectedTeleBg.option };
    } catch (error) {
        console.error('Error retrieving background config:', error);
        return { file: null, bgOption: 'no-background' };
    }
}

export async function storeFile(file: File): Promise<string> {
    const fileHandleId = `fileHandle-${Date.now()}`;

    try {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onloadend = async () => {
                try {
                    const fileDataUrl = reader.result as string;

                    const existingConfig: BackgroundConfig = await getItemFromMisc(
                        getMActiveId() || '',
                        MISC_KEYS.backgroundConfig,
                    );

                    const updatedConfig: BackgroundConfig = {
                        ...existingConfig,
                        backgroundImage: {
                            id: fileHandleId,
                            name: file.name,
                            type: file.type,
                            dataUrl: fileDataUrl,
                        },
                    };
                    await updateItemInMisc(
                        getMActiveId() || '',
                        MISC_KEYS.backgroundConfig,
                        updatedConfig,
                    );
                    resolve(fileHandleId);
                } catch (error) {
                    console.error('Error storing file in IndexedDB:', error);
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(new Error('FileReader failed to read the file'));
            };
        });
    } catch (error) {
        console.error('Error storing file in IndexedDB:', error);
        throw error;
    }
}
export async function setLastBgSelection(option: string): Promise<void> {
    try {
        const existingConfig: BackgroundConfig = await getItemFromMisc(
            getMActiveId() || '',
            MISC_KEYS.backgroundConfig,
        );

        const updatedConfig: BackgroundConfig = {
            ...existingConfig,
            lastSelectedTeleBg: {
                option: option,
            },
        };

        await updateItemInMisc(getMActiveId() || '', MISC_KEYS.backgroundConfig, updatedConfig);
    } catch (error) {
        console.error('Error setting the option in idb:', error);
    }
}

//for future reference if needed

// export async function removeStoreHandle(): Promise<void> {
//     try {
//         console.log('Removing file from IndexedDB');

//         // Remove the file metadata and data URL (base64) from IndexedDB
//         await getTable(DB_TABLES.DB_TABLE_MISC)?.delete('backgroundImage');
//     } catch (error) {
//         console.error('Error removing file from IndexedDB:', error);
//         throw error;
//     }
// }
